import { defineMessages, type IntlShape } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import {
	ATLASSIAN_ACCOUNT_ID,
	type Snippet,
	type SnippetOwner,
	type SnippetScope,
} from './snippetTypes.ts';

const messages = defineMessages({
	snippetBy: {
		id: 'snippets.quick-insert.by',
		defaultMessage: 'by {name}',
		description: 'Description text showing who created the snippet, with the creator name',
	},
	template: {
		id: 'snippets.quick-insert.template',
		defaultMessage: 'template',
		description: 'Keyword for template search in quick insert',
	},
});

function filterValidAdfNodes(nodes: (ADFEntity | null | undefined)[]): ADFEntity[] {
	return nodes.filter((node): node is ADFEntity => node !== undefined && node !== null);
}

export const parseSnippetContent = (content: string): ADFEntity | ADFEntity[] | undefined => {
	try {
		const parsedContent = JSON.parse(content);

		// Handle fragment (array of nodes without doc wrapper)
		if (Array.isArray(parsedContent)) {
			return filterValidAdfNodes(parsedContent);
		}

		// Handle single node or doc node
		const adfContent = parsedContent as ADFEntity;

		if (adfContent.type === 'doc' && Array.isArray(adfContent.content)) {
			return filterValidAdfNodes(adfContent.content);
		}

		return adfContent;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error processing snippet ADF:', error);
		return undefined;
	}
};

/** Prefix for snippet quick insert item keys in the editor. */
const SNIPPET_QUICK_INSERT_KEY_PREFIX = 'snippet-extension:snippet-';

/**
 * Returns the quick insert menu key for a snippet. Use this when adding, updating,
 * or removing snippet items from the editor's quick insert.
 */
export const getSnippetQuickInsertKey = (snippetId: string): string =>
	`${SNIPPET_QUICK_INSERT_KEY_PREFIX}${snippetId}`;

export const extractSnippetKeywords = (name: string, intl: IntlShape): string[] => {
	const nameKeywords = name
		.split(/\s+/)
		.map((word) => word.toLowerCase().trim())
		.filter((word) => word.length > 0);

	const templateKeyword = intl.formatMessage(messages.template).toLowerCase();
	const allKeywords = [...nameKeywords, templateKeyword];
	return Array.from(new Set(allKeywords));
};

export const createSnippetDescription = (createdBy: SnippetOwner, intl: IntlShape): string => {
	const name = createdBy.displayName || createdBy.accountId;
	return intl.formatMessage(messages.snippetBy, { name });
};

export const wrapAdfWithDocNode = (adf: ADFEntity | ADFEntity[]): DocNode => {
	const content: any | any[] = Array.isArray(adf) ? adf : [adf];
	return {
		version: 1,
		type: 'doc',
		content,
	};
};

/**
 * Returns the analytics scope for a snippet based on who created it.
 * - 'global' for Atlassian-owned templates
 * - 'private' for user-created private templates
 * - TODO: 'shared' for user-created site-wide templates once the backend supports visibility
 */
export const getSnippetScope = (snippet: Snippet): SnippetScope => {
	if (snippet.createdBy.accountId === ATLASSIAN_ACCOUNT_ID) {
		return 'global';
	}
	// TODO: When backend returns visibility/scope, return 'shared' for site-wide templates
	return 'private';
};

/**
 * Returns a suggested name for a duplicated snippet.
 * - If currentName ends with optional space(s) + number (e.g. "Snippet 2", "Snippet 10"), increment the number (e.g. "Snippet 3").
 * - Otherwise append " 2" so the next duplicate will fall under the first case.
 */
export const getSuggestedDuplicateName = (currentName: string): string => {
	const trimmed = currentName.trim();
	if (!trimmed) {
		// If the name is empty, leave as is
		return currentName;
	}
	// Match "prefix" + space(s) + trailing number (e.g. "Snippet 2")
	const match = trimmed.match(/^(.+?)\s+(\d+)$/);
	if (match) {
		const prefix = match[1];
		const num = match[2];
		return `${prefix} ${parseInt(num, 10) + 1}`;
	}
	return `${trimmed} 2`;
};
